import { useCallback, useMemo, useState } from 'react';

function questionIndex(step) {
  const match = /^q(\d+)$/.exec(step);
  return match ? Number(match[1]) : -1;
}

function canAdvanceFor(question, value) {
  if (!question) return false;
  if (question.type === 'openlist') {
    return Array.isArray(value) && value.length > 0;
  }
  if (question.type === 'location') {
    // v2.0.2: multi-select shape. Legacy shape (value.text) is still accepted
    // so any in-flight setup mid-wizard doesn't dead-end.
    if (Array.isArray(value?.items)) return value.items.length > 0;
    return Boolean(value && String(value.text || '').trim());
  }
  if (question.type === 'freetext') {
    return Boolean(String(value || '').trim());
  }
  if (question.type === 'narrowfilters') {
    // All filters default to "any" — the step is always advanceable, even with nothing touched.
    return true;
  }
  if (question.type === 'pickone_editable') {
    if (!value || !Array.isArray(value.options)) return false;
    if (value.options.length === 0) return false;
    if (typeof value.selectedIndex !== 'number') return false;
    const picked = value.options[value.selectedIndex];
    return Boolean(picked && String(picked).trim());
  }
  return false;
}

const LOCKED_STEPS = new Set(['discovering', 'finding', 'launched']);
// (v2.0.2 — Test step dropped; wizard goes messages → finding directly.)

function deriveAnswersFromCampaign(campaign) {
  const briefAnswers = campaign?.brief?.answers;
  if (briefAnswers && typeof briefAnswers === 'object') {
    return { ...briefAnswers };
  }

  const targeting = campaign?.targeting || {};
  const who = Array.isArray(targeting.titles) && targeting.titles.length
    ? targeting.titles
    : (Array.isArray(targeting.keywords) ? targeting.keywords : []);
  const locations = Array.isArray(targeting.locations) && targeting.locations.length
    ? targeting.locations
    : (targeting.location ? [targeting.location] : []);
  const industries = Array.isArray(targeting.industries) ? targeting.industries : [];

  return {
    who,
    where: { items: locations },
    industries,
    priorityCompanies: Array.isArray(campaign?.priorityCompanies) ? campaign.priorityCompanies : [],
    goal: typeof campaign?.goal === 'string' ? campaign.goal : '',
    maxTouches: Number(campaign?.maxTouches || 6),
    perEpisodeBudget: Number(campaign?.perEpisodeBudget || 10),
    silenceDays: Number(campaign?.silenceDays || 14),
    endAt: campaign?.endAt || null,
    exploreShare: typeof campaign?.exploreShare === 'number' ? campaign.exploreShare : 0.3,
    replyConfidenceThreshold:
      typeof campaign?.replyConfidenceThreshold === 'number' ? campaign.replyConfidenceThreshold : 0.8,
    bookingDays: Array.isArray(campaign?.bookingDays) ? campaign.bookingDays : [],
  };
}

export default function useSetupWizard() {
  const [flow, setFlowState] = useState(null);
  const [step, setStepState] = useState('launcher');
  const [answers, setAnswers] = useState({}); // includes optional priorityCompanies (Q5)
  const [proposal, setProposal] = useState(null);
  const [messages, setMessages] = useState(null); // { touches }
  const [manualProspects, setManualProspects] = useState([]);
  const [selectedProspectIds, setSelectedProspectIdsState] = useState(() => new Set());
  // Per-prospect edits made on the Prospects screen (opener text, manual coaching notes).
  // Merged into selectedProspects at approve time. Cleared on reset / setFlow.
  const [prospectDrafts, setProspectDraftsState] = useState({});
  const [error, setError] = useState(null);
  const isCoachFlow = String(flow?.id || '').toLowerCase() === 'exec_coach';

  const setStep = useCallback((nextStepOrUpdater) => {
    setStepState((current) => {
      const next = typeof nextStepOrUpdater === 'function'
        ? nextStepOrUpdater(current)
        : nextStepOrUpdater;
      if (!isCoachFlow) return next;
      if (next === 'launcher' || next === 'today' || next === 'design' || next === 'launched') return next;
      if (/^q\d+$/.test(String(next || ''))) return 'design';
      return 'design';
    });
  }, [isCoachFlow]);

  const setSelectedProspectIds = useCallback((ids) => {
    setSelectedProspectIdsState(ids instanceof Set ? ids : new Set(ids || []));
  }, []);

  const setFlow = useCallback((nextFlow) => {
    setFlowState(nextFlow);
    setAnswers({});
    setProposal(null);
    setMessages(null);
    setManualProspects([]);
    setSelectedProspectIdsState(new Set());
    setProspectDraftsState({});
    setError(null);
  }, []);

  const setProspectDraft = useCallback((externalId, patch) => {
    const key = String(externalId);
    setProspectDraftsState((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), ...patch },
    }));
  }, []);

  const setAnswer = useCallback((key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  }, []);

  const initFromCampaign = useCallback(({ campaign, touches = [], strategyNote = '' }) => {
    setAnswers(deriveAnswersFromCampaign(campaign));
    setProposal({
      campaign,
      touches: Array.isArray(touches) ? touches : [],
      strategyNote: String(strategyNote || campaign?.strategyNote || campaign?.brief?.generated?.strategyNote || '').trim(),
    });
    setManualProspects([]);
    setSelectedProspectIdsState(new Set());
    setProspectDraftsState({});
    setError(null);
  }, []);

  const addManualProspect = useCallback((prospect) => {
    setManualProspects((prev) => [...prev, prospect]);
    setSelectedProspectIdsState((prev) => {
      const next = new Set(prev);
      next.add(String(prospect.externalId));
      return next;
    });
  }, []);

  const toggleProspect = useCallback((id) => {
    const key = String(id);
    setSelectedProspectIdsState((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setFlowState(null);
    setAnswers({});
    setProposal(null);
    setMessages(null);
    setManualProspects([]);
    setSelectedProspectIdsState(new Set());
    setProspectDraftsState({});
    setError(null);
    setStepState('launcher');
  }, []);

  const goToDiscovering = useCallback(() => setStep('discovering'), [setStep]);
  const goToProspects = useCallback(() => setStep('prospects'), [setStep]);
  const goToMessages = useCallback(() => setStep('messages'), [setStep]);
  const goToFinding = useCallback(() => setStep('finding'), [setStep]);
  const goToLaunched = useCallback(() => setStep('launched'), [setStep]);

  const next = useCallback(() => {
    setStep((current) => {
      if (current === 'launcher') return 'design';
      if (current === 'design') return isCoachFlow ? 'launched' : 'discovering';
      // Legacy per-question steps (q0/q1/q2) — still supported so nothing breaks if
      // some entry point sets them, but the primary flow uses 'design' now.
      const qIdx = questionIndex(current);
      if (qIdx >= 0) {
        const total = flow?.questions?.length || 0;
        if (qIdx + 1 >= total) return isCoachFlow ? 'launched' : 'discovering';
        return `q${qIdx + 1}`;
      }
      return current;
    });
  }, [flow, isCoachFlow, setStep]);

  const back = useCallback(() => {
    setStep((current) => {
      if (isCoachFlow) {
        if (current === 'launched') return 'design';
        if (current === 'design') return 'launcher';
        if (current === 'launcher' || current === 'today') return current;
        return 'design';
      }
      if (current === 'design') return 'launcher';
      if (LOCKED_STEPS.has(current) || current === 'launcher' || current === 'prospects' || current === 'messages') {
        if (current === 'prospects') return 'launcher';
        return current;
      }
      const qIdx = questionIndex(current);
      if (qIdx === 0) return 'launcher';
      if (qIdx > 0) return `q${qIdx - 1}`;
      return current;
    });
  }, [isCoachFlow, setStep]);

  const canAdvance = useMemo(() => {
    // On the combined design step, gate on ALL questions being valid.
    if (step === 'design') {
      const list = flow?.questions || [];
      if (!list.length) return false;
      return list.every((q) => canAdvanceFor(q, answers[q.key]));
    }
    const qIdx = questionIndex(step);
    if (qIdx < 0) return step === 'launcher';
    const question = flow?.questions?.[qIdx];
    if (!question) return false;
    return canAdvanceFor(question, answers[question.key]);
  }, [step, flow, answers]);

  return {
    flow,
    step,
    answers,
    proposal,
    messages,
    manualProspects,
    selectedProspectIds,
    prospectDrafts,
    error,
    setFlow,
    setAnswer,
    initFromCampaign,
    setProposal,
    setMessages,
    addManualProspect,
    toggleProspect,
    setSelectedProspectIds,
    setProspectDraft,
    setError,
    setStep,
    next,
    back,
    reset,
    canAdvance,
    goToDiscovering,
    goToProspects,
    goToMessages,
    goToFinding,
    goToLaunched,
  };
}
